import type { PublicProfessional } from '@/features/professionals/services/professional-public.service';
import { resolveUploadUrl } from '@/lib/uploads';

import { usePublicSeoConfig } from '../hooks/useSeoConfig';
import { buildConsultantJsonLd } from '../lib/jsonLd';
import { resolveSeoTemplate, truncateDescription, withTitleSuffix } from '../lib/seoUtils';
import { JsonLd } from './JsonLd';
import { Seo } from './Seo';

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').replace(/\s+/g, ' ').trim();
}

/** Meta tags for the public consultant profile, generated from the admin template. */
export function ConsultantSeo({ professional }: { professional: PublicProfessional }) {
  const { data: config } = usePublicSeoConfig();
  if (!config) return null;

  const user = professional.userId;
  const nome = `${user?.profile.firstName ?? ''} ${user?.profile.lastName ?? ''}`.trim();
  const values = {
    nome,
    titolo: professional.title,
    specializzazioni: professional.specializations.join(', '),
    bio: professional.profileDescription ? stripHtml(professional.profileDescription) : '',
  };

  const title = withTitleSuffix(
    resolveSeoTemplate(config.consultant.titleTemplate, values) || config.global.defaultTitle,
    config
  );
  const description =
    truncateDescription(resolveSeoTemplate(config.consultant.descriptionTemplate, values), 300) ||
    config.global.defaultDescription;
  const avatar = user?.profile.avatar ? resolveUploadUrl(user.profile.avatar) : undefined;

  return (
    <>
      <Seo
        title={title}
        description={description}
        keywords={[...professional.specializations, ...config.global.defaultKeywords]}
        ogImage={avatar || config.global.defaultOgImage}
        ogType="profile"
      />
      <JsonLd data={[buildConsultantJsonLd(config, professional, window.location.href)]} />
    </>
  );
}
